import React, { useState } from 'react';
import { Heart, Shield, Check, ArrowRight } from 'lucide-react';
import { donationTiers } from '../mock';
import { toast } from '../hooks/use-toast';

const Donate = () => {
  const [selected, setSelected] = useState(15);
  const [custom, setCustom] = useState('');
  const [monthly, setMonthly] = useState(true);

  const handleDonate = () => {
    const amount = custom ? Number(custom) : selected;
    if (!amount || amount < 1) {
      toast({ title: 'Please enter a valid amount', variant: 'destructive' });
      return;
    }
    toast({
      title: 'Thank you for taking a stand!',
      description: `Your £${amount} ${monthly ? 'monthly' : 'one-time'} donation will fund holistic mental health care. (demo)`,
    });
  };

  return (
    <section id="donate" className="py-24 lg:py-32 bg-[#F7F7F8] relative overflow-hidden">
      <div className="brush-swash w-96 h-20 top-16 -left-16" />

      <div className="max-w-6xl mx-auto px-5 lg:px-8 relative">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#6e12ed] mb-4">Donate Today</div>
          <h2 className="font-serif text-4xl lg:text-6xl font-bold text-[#0E1112] leading-[1.02]">
            Your £5 keeps someone’s<br />
            <span className="hl-box hl-cyan">healing</span> journey alive.
          </h2>
          <p className="mt-5 text-neutral-700 text-lg">
            100% of gifts fuel our holistic mental health services. Every regular donor
            receives our exclusive Take a Stand short film and early access to the ACCI shop.
          </p>
        </div>

        <div className="bg-white rounded-[2rem] shadow-xl border border-black/5 overflow-hidden">
          <div className="grid lg:grid-cols-5">
            <div className="lg:col-span-3 p-8 lg:p-12">
              <div className="inline-flex bg-[#F3F3F5] rounded-full p-1 mb-8">
                <button
                  onClick={() => setMonthly(true)}
                  className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${
                    monthly ? 'bg-[#6e12ed] text-white shadow-sm' : 'text-neutral-600'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setMonthly(false)}
                  className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${
                    !monthly ? 'bg-[#6e12ed] text-white shadow-sm' : 'text-neutral-600'
                  }`}
                >
                  One-time
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {donationTiers.map((t) => (
                  <button
                    key={t.amount}
                    onClick={() => {
                      setSelected(t.amount);
                      setCustom('');
                    }}
                    className={`relative text-left rounded-2xl p-5 border-2 transition-all ${
                      selected === t.amount && !custom
                        ? 'border-[#6e12ed] bg-[#F8F3FF]'
                        : 'border-black/10 hover:border-black/20 bg-white'
                    }`}
                  >
                    {t.popular && (
                      <span className="absolute -top-2 right-3 text-[10px] bg-[#79c4c7] text-white font-bold uppercase tracking-wider rounded-full px-2 py-0.5">
                        Popular
                      </span>
                    )}
                    <div className="flex items-baseline gap-1">
                      <span className="font-serif text-3xl font-bold text-[#0E1112]">£{t.amount}</span>
                      <span className="text-xs text-neutral-500">{monthly ? '/mo' : ''}</span>
                    </div>
                    <div className="mt-1 font-semibold text-sm text-[#6e12ed]">{t.label}</div>
                    <div className="mt-1 text-xs text-neutral-600 leading-snug">{t.desc}</div>
                  </button>
                ))}
              </div>

              <div className="mt-5">
                <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">
                  Or enter your own amount
                </label>
                <div className="flex items-stretch gap-3">
                  <div className="relative flex-1">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 font-serif text-xl text-neutral-400">£</span>
                    <input
                      type="number"
                      min="1"
                      value={custom}
                      onChange={(e) => setCustom(e.target.value)}
                      placeholder="Custom amount"
                      className="w-full h-14 pl-10 pr-4 rounded-xl border-2 border-black/10 focus:border-[#6e12ed] focus:outline-none text-lg font-semibold bg-white"
                    />
                  </div>
                  <button
                    onClick={handleDonate}
                    className="inline-flex items-center gap-2 h-14 px-7 bg-[#6e12ed] hover:bg-[#5A0DCC] text-white font-bold rounded-xl transition-colors"
                  >
                    <Heart className="w-4 h-4 fill-white" />
                    Donate
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="mt-6 flex items-center gap-2 text-xs text-neutral-500">
                <Shield className="w-4 h-4 text-[#6e12ed]" />
                Secure donation • Registered charity • Every penny protected
              </div>
            </div>

            <div className="lg:col-span-2 bg-[#6e12ed] text-white p-8 lg:p-12 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white/10 -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-52 h-52 rounded-full bg-[#79c4c7]/40 translate-y-1/2 -translate-x-1/2 blur-2xl" />
              <div className="relative">
                <div className="text-[11px] uppercase tracking-widest text-white/70 mb-3">Your Gift Includes</div>
                <div className="font-serif text-3xl font-bold leading-tight">
                  Generational<br />healing, one<br />stand at a time.
                </div>
                <ul className="mt-8 space-y-4">
                  {[
                    'Exclusive Take a Stand short film',
                    'Super early access to the ACCI shop',
                    'Quarterly impact reports',
                    'Invites to community events',
                  ].map((li) => (
                    <li key={li} className="flex items-start gap-3 text-white/90">
                      <div className="w-6 h-6 rounded-full bg-[#79c4c7] flex items-center justify-center mt-0.5 flex-shrink-0">
                        <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                      </div>
                      <span className="text-sm leading-snug">{li}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-10 pt-6 border-t border-white/20">
                  <div className="text-[10px] uppercase tracking-widest text-white/60">Registered Charity</div>
                  <div className="font-serif text-xl font-bold mt-1">Serving Since 1987</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Donate;
